import type { User } from '../types'

const PERSISTENCE_KEY = 'workbeam_auth_persistence'

export type AuthPersistence = 'local' | 'session'

const USER_KEY = 'user'
const TOKENS_KEY = 'tokens'

export interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

function storageFor(persistence: AuthPersistence): Storage {
  return persistence === 'session' ? sessionStorage : localStorage
}

/** Where the active session lives; marker is always in localStorage. */
export function getAuthPersistence(): AuthPersistence {
  try {
    const p = localStorage.getItem(PERSISTENCE_KEY)
    if (p === 'session' || p === 'local') return p
    // Legacy installs: tokens only in localStorage, no marker yet
    if (localStorage.getItem(TOKENS_KEY)) return 'local'
    return 'local'
  } catch {
    return 'local'
  }
}

export function readPersistedUser(): User | null {
  try {
    const raw = storageFor(getAuthPersistence()).getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as User) : null
  } catch {
    return null
  }
}

export function readPersistedTokens(): AuthTokens | null {
  try {
    const raw = storageFor(getAuthPersistence()).getItem(TOKENS_KEY)
    return raw ? (JSON.parse(raw) as AuthTokens) : null
  } catch {
    return null
  }
}

/** New login / session mode change: write to one storage and strip the other. */
export function establishAuthSession(
  user: User,
  tokens: AuthTokens,
  rememberMe: boolean
): void {
  const persistence: AuthPersistence = rememberMe ? 'local' : 'session'
  const primary = storageFor(persistence)
  const other = storageFor(persistence === 'local' ? 'session' : 'local')
  other.removeItem(USER_KEY)
  other.removeItem(TOKENS_KEY)
  localStorage.setItem(PERSISTENCE_KEY, persistence)
  primary.setItem(USER_KEY, JSON.stringify(user))
  primary.setItem(TOKENS_KEY, JSON.stringify(tokens))
}

/** Token refresh or profile update: same persistence bucket as current session. */
export function saveUserAndTokensToActiveStorage(user: User, tokens: AuthTokens): void {
  const s = storageFor(getAuthPersistence())
  s.setItem(USER_KEY, JSON.stringify(user))
  s.setItem(TOKENS_KEY, JSON.stringify(tokens))
}

export function clearAllAuthStorage(): void {
  try {
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(TOKENS_KEY)
    sessionStorage.removeItem(USER_KEY)
    sessionStorage.removeItem(TOKENS_KEY)
    localStorage.removeItem(PERSISTENCE_KEY)
  } catch {
    /* ignore */
  }
}
